import {
  getAuthorization,
  getCompanyId,
  setAuthorization,
} from "../Functions/AuthFunction";
import {
  ref,
  get,
  query,
  orderByChild,
  equalTo,
  update,
  set,
} from "firebase/database";
import { storage, db } from "./firebase";
import { v4 as uuidv4 } from "uuid";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import bcrypt from "bcryptjs";
import { sendOTP } from "./mailbox";
import { createFreeTrialSubscription } from "../Constants/subscription";
import { isFormActive } from "../Constants/formStatus";

export const registerCompany = async (payload) => {
  const companiesRootPath = "contacthq/companies";
  let data =
    payload instanceof FormData
      ? Object.fromEntries(payload.entries())
      : payload;
  const { email, companyName, password, name } = data;
  if (!email || !companyName || !password) {
    return {
      success: false,
      message: "Required fields (Email, Company Name, Password) are missing.",
    };
  }
  try {
    const companiesRef = ref(db, companiesRootPath);
    const emailCheck = query(
      companiesRef,
      orderByChild("email"),
      equalTo(email)
    );
    const nameCheck = query(
      companiesRef,
      orderByChild("companyName"),
      equalTo(companyName)
    );
    const [emailSnap, nameSnap] = await Promise.all([
      get(emailCheck),
      get(nameCheck),
    ]);
    if (emailSnap.exists())
      return { success: false, message: "This email is already registered." };
    if (nameSnap.exists())
      return { success: false, message: "This company name is already taken." };
    const companyId = uuidv4();
    const token = uuidv4();
    const hashedPassword = bcrypt.hashSync(password, 10);
    const otp = Math.floor(100000 + Math.random() * 900000).toString();
    const otpExpiry = new Date(Date.now() + 15 * 60 * 1000).toISOString();
    let profilePicURL = "";
    if (
      data.profilePic &&
      data.profilePic instanceof File &&
      data.profilePic.size > 0
    ) {
      if (data.profilePic.size <= 2 * 1024 * 1024) {
        const picRef = storageRef(
          storage,
          `${companiesRootPath}/${companyId}/logo`
        );
        await uploadBytes(picRef, data.profilePic);
        profilePicURL = await getDownloadURL(picRef);
      }
    }
    const now = new Date();
    const trialSubscription = createFreeTrialSubscription(now);
    // Do not spread raw payload (may include File / plaintext password twice)
    const {
      profilePic: _profilePic,
      password: _plainPassword,
      confirmPassword: _confirm,
      ...safeFields
    } = data;
    const newCompany = {
      ...safeFields,
      companyId,
      email,
      companyName,
      name: name || "",
      password: hashedPassword,
      profilePicURL,
      token,
      otp,
      otpExpiry: otpExpiry,
      status: false,
      createdAt: now.toISOString(),
      trialEndsAt: trialSubscription.expiresAt,
      subscription: trialSubscription,
    };
    await set(ref(db, `${companiesRootPath}/${companyId}`), newCompany);
    try {
      await sendOTP({ name, email, otp, otpExpiry });
    } catch (mailError) {
      console.error("Email failed to send, but user was created:", mailError);
    }
    return {
      success: true,
      message: "Registration successful! Please check your email for the OTP.",
      data: { email, token },
    };
  } catch (error) {
    console.error("Registration error:", error);
    return {
      success: false,
      message: "An unexpected error occurred. Please try again.",
    };
  }
};
export const verifyOTP = async (email, otp) => {
  const companiesRootPath = "contacthq/companies";
  const companiesRef = ref(db, companiesRootPath);
  try {
    const emailQuery = query(
      companiesRef,
      orderByChild("email"),
      equalTo(email)
    );
    const snapshot = await get(emailQuery);
    if (!snapshot.exists()) {
      return { success: false, message: "User not found." };
    }
    const companyId = Object.keys(snapshot.val())[0];
    const companyData = snapshot.val()[companyId];

    if (!companyData.otp || companyData.otp !== otp) {
      return { success: false, message: "Invalid OTP code." };
    }
    const now = new Date();
    const expiry = new Date(companyData.otpExpiry);
    if (now > expiry) {
      return {
        success: false,
        message: "OTP has expired. Please request a new one.",
      };
    }
    const updates = {};
    updates[`${companiesRootPath}/${companyId}/status`] = true;
    updates[`${companiesRootPath}/${companyId}/otp`] = null;
    updates[`${companiesRootPath}/${companyId}/otpExpiry`] = null;
    await update(ref(db), updates);
    let sendData = {
      companyId: companyData.companyId,
      businessType: companyData.businessType,
      companySize: companyData.companySize,
      country: companyData.country,
      name: companyData.name,
      email: companyData.email,
      token: companyData.token,
      mobile: companyData.mobile,
      websiteUrl: companyData.websiteUrl,
      companyName: companyData.companyName,
      profilePicURL: companyData.profilePicURL,
      subscription: companyData.subscription,
      trialEndsAt: companyData.trialEndsAt,
      status: companyData.status,
      joinedDate: companyData.createdAt,
      lastLoginAt: companyData.lastLoginAt,
      role: companyData.role || "admin",
    };
    return {
      success: true,
      message: "Email verified successfully!",
      data: sendData,
    };
  } catch (error) {
    console.error("Verify OTP Failed:", error);
    return {
      success: false,
      message: "Verification failed. Please try again.",
    };
  }
};
export const resendOTP = async (email) => {
  const companiesRootPath = "contacthq/companies";
  const companiesRef = ref(db, companiesRootPath);
  try {
    const emailQuery = query(
      companiesRef,
      orderByChild("email"),
      equalTo(email)
    );
    const snapshot = await get(emailQuery);
    if (!snapshot.exists()) {
      return { success: false, message: "User not found." };
    }
    const companyId = Object.keys(snapshot.val())[0];
    const companyData = snapshot.val()[companyId];
    const newOtp = Math.floor(100000 + Math.random() * 900000).toString();
    const newExpiry = new Date(Date.now() + 15 * 60 * 1000).toISOString();
    const updates = {};
    updates[`${companiesRootPath}/${companyId}/otp`] = newOtp;
    updates[`${companiesRootPath}/${companyId}/otpExpiry`] = newExpiry;
    await update(ref(db), updates);
    await sendOTP({
      name: companyData.name,
      email: companyData.email,
      otp: newOtp,
      otpExpiry: newExpiry,
    });
    return { success: true, message: "New code sent!" };
  } catch (error) {
    console.error("Resend OTP Error:", error);
    return { success: false, message: "Failed to resend code." };
  }
};
export const loginCompany = async ({ email, password }) => {
  if (!email || !password) {
    return { success: false, message: "Email and password are required" };
  }
  try {
    const companiesRef = ref(db, "contacthq/companies");
    const companyQuery = query(
      companiesRef,
      orderByChild("email"),
      equalTo(email)
    );
    const companySnapshot = await get(companyQuery);
    if (!companySnapshot.exists()) {
      return { success: false, message: "No account found with this email." };
    }
    const companies = companySnapshot.val();
    const companyKey = Object.keys(companies)[0];
    const company = companies[companyKey];
    const isPasswordValid = bcrypt.compareSync(password, company.password);
    if (!isPasswordValid) {
      return { success: false, message: "Incorrect password!" };
    }
    if (company.status === false) {
      return {
        success: false,
        message:
          "Account not verified. Please verify your email using the OTP.",
        needsVerification: true,
      };
    }
    const now = new Date().toISOString();
    const newToken = uuidv4();
    await update(ref(db, `contacthq/companies/${companyKey}`), {
      lastLoginAt: now,
      token: newToken,
    });
    let sendData = {
      companyId: company.companyId,
      businessType: company.businessType,
      companySize: company.companySize,
      country: company.country,
      name: company.name,
      email: company.email,
      token: newToken,
      mobile: company.mobile,
      websiteUrl: company.websiteUrl,
      companyName: company.companyName,
      profilePicURL: company.profilePicURL,
      subscription: company.subscription,
      trialEndsAt: company.trialEndsAt,
      status: company.status,
      joinedDate: company.createdAt,
      lastLoginAt: now,
      role: company.role || "admin",
    };
    setAuthorization(newToken, sendData.companyId);
    return {
      success: true,
      message: "Login successful!",
      data: sendData,
    };
  } catch (error) {
    console.error("🔥 Login failed:", error);
    return {
      success: false,
      message: "An error occurred during login. Please try again.",
    };
  }
};
export const requestPasswordReset = async (email) => {
  const companiesRootPath = "contacthq/companies";
  const companiesRef = ref(db, companiesRootPath);
  try {
    const emailQuery = query(
      companiesRef,
      orderByChild("email"),
      equalTo(email)
    );
    const snapshot = await get(emailQuery);
    if (!snapshot.exists()) {
      return { success: false, message: "No account found with this email." };
    }
    const otp = Math.floor(100000 + Math.random() * 900000).toString();
    const expiryTime = new Date(Date.now() + 15 * 60 * 1000).toISOString();
    const companyId = Object.keys(snapshot.val())[0];
    const companyData = snapshot.val()[companyId];
    const companyUpdateRef = ref(db, `${companiesRootPath}/${companyId}`);
    await update(companyUpdateRef, {
      otp: otp,
      otpExpiry: expiryTime,
    });
    const emailData = {
      email: companyData.email,
      name: companyData.name,
      otp: otp,
      otpExpiry: expiryTime,
    };
    const mailResult = await sendOTP(emailData);
    if (!mailResult.success) {
      throw new Error("Could not send email. Please try again later.");
    }
    return {
      success: true,
      message: "A verification code has been sent to your email.",
      data: { email: companyData.email },
    };
  } catch (error) {
    console.error("🔥 Password Reset Request Failed:", error);
    return {
      success: false,
      message: error.message || "Failed to process request.",
    };
  }
};
export const resetPassword = async (email, newPassword) => {
  const companiesRootPath = "contacthq/companies";
  const companiesRef = ref(db, companiesRootPath);
  try {
    const emailQuery = query(
      companiesRef,
      orderByChild("email"),
      equalTo(email)
    );
    const snapshot = await get(emailQuery);
    if (!snapshot.exists()) {
      return { success: false, message: "Account not found." };
    }
    const companyId = Object.keys(snapshot.val())[0];
    const cleanId = String(companyId).replace(/"/g, "");
    const hashedPassword = bcrypt.hashSync(newPassword, 10);
    const newToken = uuidv4();
    const companyUpdateRef = ref(db, `${companiesRootPath}/${cleanId}`);
    await update(companyUpdateRef, {
      password: hashedPassword,
      token: newToken,
      resetOTP: null,
      resetOTPExpiry: null,
      lastPasswordChange: new Date().toISOString(),
    });
    return {
      success: true,
      message:
        "Your password has been reset successfully. Please log in with your new password.",
    };
  } catch (error) {
    console.error("🔥 Reset Password Failed:", error);
    return {
      success: false,
      message:
        "An error occurred while updating your password. Please try again.",
    };
  }
};
export const verifyAuth = async () => {
  const companyId = getCompanyId();
  const token = getAuthorization();
  if (!companyId || !token) {
    return { success: false, error: "Missing Company ID or Token" };
  }
  try {
    const companyRef = ref(db, `contacthq/companies/${companyId}`);
    const snapshot = await get(companyRef);
    if (!snapshot.exists()) {
      return { success: false, error: "Company account not found." };
    }
    const companyData = snapshot.val();
    if (companyData.token !== token) {
      return {
        success: false,
        error: "Unauthorized: Session expired or invalid.",
      };
    }
    if (!companyData.status) {
      return { success: false, error: "Account is inactive/unverified." };
    }
    return {
      success: true,
      companyData: { ...companyData, companyId },
    };
  } catch (error) {
    console.error("🔥 Auth verification failed:", error);
    return { success: false, error: "Authentication service error." };
  }
};
export const verifyCompanyEmail = async (token) => {
  const companiesRootPath = "contacthq/companies";
  const companiesRef = ref(db, companiesRootPath);
  try {
    const tokenQuery = query(
      companiesRef,
      orderByChild("token"),
      equalTo(token)
    );
    const snapshot = await get(tokenQuery);
    if (!snapshot.exists()) {
      return {
        success: false,
        message: "Invalid or expired verification link.",
      };
    }
    const companyId = Object.keys(snapshot.val())[0];
    const companyData = snapshot.val()[companyId];
    if (companyData.status === true) {
      setAuthorization(token, companyId);
      return { success: true, message: "Account already verified." };
    }
    const companyRef = ref(db, `${companiesRootPath}/${companyId}`);
    await update(companyRef, {
      status: true,
      emailVerifiedAt: new Date().toISOString(),
    });
    setAuthorization(token, companyId);
    return {
      success: true,
      message: "Email verified successfully!",
      data: { ...companyData, status: true, companyId },
    };
  } catch (error) {
    console.error("🔥 Verification failed:", error);
    return {
      success: false,
      message: "Verification failed. Please try again.",
    };
  }
};
export const subscriptionCheckAction = async (companyData) => {
  try {
    const subscription = companyData.subscription || {};
    const expiresAt = subscription.expiresAt;
    const companyId = companyData.companyId;
    const formLimit = parseInt(subscription.formLimit, 10) || 1;
    const formsRef = ref(db, "contacthq/forms");
    const formsQuery = query(
      formsRef,
      orderByChild("companyId"),
      equalTo(companyId)
    );
    const formsSnapshot = await get(formsQuery);
    if (!formsSnapshot.exists()) {
      return { success: true, expired: false, message: "No forms found." };
    }
    const allForms = [];
    formsSnapshot.forEach((child) => {
      allForms.push({ id: child.key, ...child.val() });
    });
    const updates = {};
    let hasUpdates = false;
    if (expiresAt && new Date(expiresAt) < new Date()) {
      let pausedCount = 0;
      allForms.forEach((form) => {
        if (isFormActive(form.status)) {
          updates[`contacthq/forms/${form.id}/status`] = "paused";
          hasUpdates = true;
          pausedCount++;
        }
      });
      if (hasUpdates) await update(ref(db), updates);
      return {
        success: true,
        expired: true,
        message: `Subscription expired. ${pausedCount} forms paused.`,
      };
    }
    const activeForms = allForms.filter((f) => isFormActive(f.status));
    if (activeForms.length > formLimit) {
      activeForms.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      const formsToPause = activeForms.slice(formLimit);
      formsToPause.forEach((form) => {
        updates[`contacthq/forms/${form.id}/status`] = "paused";
        hasUpdates = true;
      });
      if (hasUpdates) {
        await update(ref(db), updates);
        return {
          success: true,
          expired: false,
          limitEnforced: true,
          message: `Plan limit exceeded. ${formsToPause.length} oldest forms were auto-paused.`,
        };
      }
    }
    return {
      success: true,
      expired: false,
      message: "Subscription is active.",
    };
  } catch (error) {
    console.error("Subscription Check Error:", error);
    return {
      success: false,
      expired: false,
      message: error.message || "Failed to check subscription status.",
    };
  }
};
